import { createRef, RefObject, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip } from '@mui/material';

import { ctxPluto } from '@/contexts/PlutoContext';
import { ctxPopup } from '@/contexts/PopupContext';
import { ctxRoutes } from '@/contexts/RoutesCtx';
import { RouteGrouper } from '@/components/routes/RouteGrouper';
import { RoutesSummaryHeader } from '@/components/routes/RoutesSummaryHeader';
import { RoutesSummary } from '@/components/routes/RoutesSummary';
import { RoutesResultHeader } from '@/components/routes/RoutesResultHeader';
import RouteList from '@/components/routes/RouteList';
import { SelectedRoute } from '@/types';
import { useNotificationStore } from '@/stores/notificationstore';
import { fulldate } from '@/utilities/dateUtils';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { DetailedRoute, MinimalTarget } from '@/generated/model';
import { scrollToElement, useRouteListScroll } from '@/hooks/useRouteListScroll';
import { useRoutesQuery } from '@/components/routes/api/useRoutesQuery';

// eslint-disable-next-line react-refresh/only-export-components
export function convertRouteToSelected(route: DetailedRoute, selectedTarget: MinimalTarget): SelectedRoute {
  return {
    id: route.id,
    target: selectedTarget,
    fromTimeMicros: route.startPosition.timeMicros,
    toTimeMicros: route.stopPosition.timeMicros,
  } satisfies SelectedRoute;
}

function convertRouteListToSelected(
  routes: DetailedRoute[] | undefined,
  selectedTarget: MinimalTarget | undefined
): SelectedRoute[] {
  if (!routes || !selectedTarget) return [];
  return routes.map((r) => convertRouteToSelected(r, selectedTarget));
}

function stripYear(d: string) {
  if (!/^20\d\d-/.test(d)) return d;
  return d.substring(5);
}

function QuickNav({ label, current, onClickScroll }: { label: string; current: boolean; onClickScroll: () => void }) {
  return (
    <Chip
      sx={{
        display: 'flex',
        marginBottom: '2px',
        color: current ? 'var(--mui-palette-primary-contrast-text)' : 'var(--mui-palette-text-primary)',
        backgroundColor: current ? 'var(--mui-palette-primary-main)' : 'var(--mui-palette-action-selected)',
      }}
      key={label + 'nav'}
      onClick={onClickScroll}
      label={label}
    />
  );
}

export function RoutesList({ topRef }: { topRef: RefObject<HTMLDivElement | null> }) {
  const plutoCtx = useContext(ctxPluto);
  const popupCtx = useContext(ctxPopup);
  const routeCtx = useContext(ctxRoutes);
  const [selectAllChecked, setSelectAllChecked] = useState(
    plutoCtx?.selectedRoutes.length === routeCtx?.routes?.length
  );
  const { t, i18n } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const refs = useRef<Map<string, RefObject<HTMLDivElement | null>>>(new Map());
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [activeSection, setActiveSection] = useState<HTMLDivElement | null>(topRef.current);
  const [showTooltip, setShowTooltip] = useState<boolean>(
    JSON.parse(sessionStorage.getItem('showRoutesListTooltip') ?? 'true')
  );
  const { registerRef, scrollTo } = useRouteListScroll(containerRef);
  const selectedTarget = routeCtx?.selectedTarget;
  const selectedDates = routeCtx?.selectedDates;
  const setAdjustMap = routeCtx?.setAdjustMap;
  const setRoutesLoading = routeCtx?.setRoutesLoading;
  const setRoutes = routeCtx?.setRoutes;
  const setPositions = routeCtx?.setPositions;
  const setActualToTime = routeCtx?.setActialToTime;
  const setSelectedRoutes = plutoCtx?.setSelectedRoutes;
  const query = useRoutesQuery({ targetId: selectedTarget?.id, dates: selectedDates });
  const showLoader = popupCtx?.showLoader;
  const hideLoader = popupCtx?.hideLoader;

  useEffect(() => {
    if (!routeCtx?.hasBeenInitialized) {
      handleSelectAll();
      routeCtx?.setHasBeenInitialized(true);
    }
  }, []);

  useEffect(() => {
    setSelectAllChecked(plutoCtx?.selectedRoutes.length === routeCtx?.routes?.length);
  }, [plutoCtx?.selectedRoutes, routeCtx?.routes]);

  useEffect(() => {
    if (selectedDates || selectedTarget) {
      setAdjustMap?.(true);
    }
  }, [selectedDates, selectedTarget, setAdjustMap]);

  useEffect(() => {
    setRoutesLoading?.(query.isFetching);

    if (!query.isFetching) {
      return;
    }

    showLoader?.('circular');
    return () => hideLoader?.();
  }, [hideLoader, query.isFetching, setRoutesLoading, showLoader]);

  useEffect(() => {
    if (!selectedTarget || !selectedDates || !query.isSuccess || query.isFetching || !query.data || !setRoutes) {
      return;
    }

    const routes = query.data.routes;
    setRoutes(routes);
    setPositions?.(query.data.statuses);
    setActualToTime?.(query.data.actualToTime);
    setSelectedRoutes?.(convertRouteListToSelected(routes, selectedTarget));

    if (query.data.routeMetadata.originalNoOfRoutes !== routes.length) {
      const toDateTime = fulldate(query.data.routeMetadata.toTimeMicros, t);
      showNotification(
        'info',
        t('routes.tooManyRoutes', {
          toTime: toDateTime,
          limit: query.data.routeMetadata.routesLimit,
          noOfRoutes: query.data.routeMetadata.originalNoOfRoutes,
          interpolation: { escapeValue: false },
        })
      );
    }
  }, [
    query.data,
    query.isFetching,
    query.isSuccess,
    selectedDates,
    selectedTarget,
    setActualToTime,
    setPositions,
    setRoutes,
    setSelectedRoutes,
    showNotification,
    t,
  ]);

  useEffect(() => {
    if (routeCtx?.clickedRouteId && routeCtx.clickedRouteId > 0) {
      scrollTo(routeCtx.clickedRouteId);
    }
  }, [routeCtx?.clickedRouteId, scrollTo]);

  function filterEmptyDays() {
    const result: RefObject<HTMLDivElement | null>[] = [];
    try {
      Object.keys(routesByTimes).map((date) => {
        const ref = refs.current.get(date);
        if (ref !== undefined && routesByTimes[date]?.[0]) result.push(ref);
      });
      // eslint-disable-next-line
    } catch (_) {}
    return result;
  }

  useEffect(() => {
    const allRefs = [topRef, ...filterEmptyDays(), bottomRef];

    const observer = new IntersectionObserver(
      (entries) => {
        if (!containerRef.current) return;
        const rContainer = containerRef.current?.getBoundingClientRect();
        let topEntryIndex: number | null = null;

        function findTopVisibleIndex() {
          for (let i = allRefs.length - 2; i >= 0; i--) {
            const el = allRefs[i].current;
            if (!el) continue;

            const r = el.getBoundingClientRect();
            if (r.y - rContainer.y < 0) {
              return i;
            }
          }

          return null;
        }

        let counter = 0;
        entries.forEach((entry) => {
          const rect = entry.boundingClientRect;
          const targetIndex = allRefs.findIndex((section) => section.current === entry.target);
          if (entry.isIntersecting) {
            if (rect.top < 0 && targetIndex !== -1) {
              if (counter === 0) topEntryIndex = targetIndex;
            }
            if (entry.target === bottomRef.current) {
              topEntryIndex = targetIndex - 1;
            }
          } else {
            if (rect.top - containerRef.current!.getBoundingClientRect().top < 0 && targetIndex !== -1) {
              topEntryIndex = targetIndex + 1;
            }
            if (entry.target === bottomRef.current) {
              topEntryIndex = findTopVisibleIndex() ?? allRefs.length - 2;
            }
          }
          counter++;
        });

        if (topEntryIndex !== null) {
          setActiveSection(allRefs[topEntryIndex].current);
        }
        if (containerRef.current?.scrollTop <= (topRef.current?.clientHeight ?? 180)) {
          setActiveSection(topRef.current);
          return;
        }
      },
      { root: containerRef.current, rootMargin: '-43px 0px 0px 0px' }
    );

    allRefs.forEach((section) => {
      if (!section.current) return;
      if (section) observer.observe(section.current);
      if (topRef.current && topRef.current.firstElementChild) observer.observe(topRef.current);
    });

    return () => observer.disconnect();
  }, [routeCtx?.routes]);

  useEffect(() => {
    if (routeCtx?.highlightedRoutes?.length) {
      setShowTooltip(false);
    }
  }, [routeCtx?.highlightedRoutes]);

  useEffect(() => {
    sessionStorage.setItem('showRoutesListTooltip', JSON.stringify(showTooltip));
  }, [showTooltip]);

  if (
    !routeCtx ||
    !routeCtx.selectedTarget ||
    !routeCtx.routes ||
    !routeCtx.selectedDates ||
    !routeCtx.selectedDates.from ||
    !routeCtx.selectedDates.to
  ) {
    return null;
  }

  function handleSelectAll() {
    if (selectAllChecked || !routeCtx) {
      plutoCtx?.setSelectedRoutes([]);
    } else {
      plutoCtx?.setSelectedRoutes(convertRouteListToSelected(routeCtx.routes, routeCtx.selectedTarget));
    }
    setSelectAllChecked(!selectAllChecked);
  }

  if (!routeCtx) return null;

  // TODO menjen useEffect-be ?
  const fromDate = routeCtx.selectedDates.from;
  const toDate = routeCtx.selectedDates.to;
  const routeGrouper = new RouteGrouper(i18n, t);
  const routesByTimes = routeGrouper.groupRoutesByTime(routeCtx.routes, fromDate, toDate);

  Object.keys(routesByTimes).map((date) => {
    if (!refs.current.get(date)) refs.current.set(date, createRef<HTMLDivElement>());
  });

  function getDaysWithRoute() {
    return Object.keys(routesByTimes).filter((date) => routesByTimes[date][0]);
  }

  function isOneDay() {
    return getDaysWithRoute().length <= 1;
  }

  function onClickScroll(ref: RefObject<HTMLDivElement | null> | undefined) {
    scrollToElement({ element: ref?.current, preferredContainer: containerRef.current, offset: 43 });
    if (ref) {
      setTimeout(() => setActiveSection(ref.current), 200);
    }
  }

  return (
    <div
      className={`route-panel-result ${!isPhoneOrHighScreen() ? 'route-panel-result-desktop' : ''}`}
      data-qa-element-id="routes_result_panel"
    >
      {!isPhoneOrHighScreen() && <RoutesResultHeader />}
      <div className={`route-panel-result-content ${routeCtx.routesLoading ? 'disabled' : ''}`}>
        <div className="route-detail-list" ref={containerRef} data-qa-element-id="route_details_list">
          {!isPhoneOrHighScreen() && <RoutesSummaryHeader />}
          <RoutesSummary ref={topRef} />
          <RouteList routesByTimes={routesByTimes} showTooltip={showTooltip} refs={refs} registerRef={registerRef} />
          <Box ref={bottomRef} sx={{ height: '1px', width: '100%', pointerEvents: 'none' }} />
        </div>
        {!isPhoneOrHighScreen() && !isOneDay() && (
          <div className="route-quick-navigation" data-qa-element-id="routes_quick_date_select_panel">
            <span data-qa-element-id="all_dates_button">
              <QuickNav
                label={t('refuels.aggregatedDataSort')}
                current={activeSection === topRef.current}
                onClickScroll={() => onClickScroll(topRef)}
              />
            </span>
            {getDaysWithRoute().map((date) => (
              <span
                data-qa-element-id={`quick_date_select_for_${date
                  .replace(/\([^)]*\)/g, '')
                  .replace(/\s*->\s*/g, '-')
                  .trim()}`}
              >
                <QuickNav
                  key={date}
                  label={stripYear(date)}
                  current={activeSection === refs.current.get(date)?.current}
                  onClickScroll={() => onClickScroll(refs.current.get(date))}
                />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
